import { useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft, Loader2, ArrowRight, CheckCircle2, XCircle, AlertCircle, Info, Minus,
  Monitor, Smartphone,
} from 'lucide-react'
import PageTransition from '@/components/PageTransition'
import GlassCard from '@/components/GlassCard'
import ScoreRing from '@/components/ScoreRing'
import ScoreDelta from '@/components/ScoreDelta'
import EmptyIllustration from '@/components/EmptyIllustration'
import { auditsApi } from '@/api/client'
import type { ReportDetail } from '@/api/client'
import { diffReports, type AuditResult } from '@/lib/diff'

function timestampOf(r: ReportDetail): number {
  return new Date(r.completedAt ?? r.createdAt).getTime()
}

function DeviceIcon({ device }: { device: ReportDetail['device'] }) {
  return device === 'DESKTOP'
    ? <Monitor size={14} className="shrink-0" style={{ color: 'var(--text-muted)' }} />
    : <Smartphone size={14} className="shrink-0" style={{ color: 'var(--text-muted)' }} />
}

function ReportHeader({ report, label }: { report: ReportDetail; label: string }) {
  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 mb-1">
        <span
          className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
          style={{ background: 'rgba(99,102,241,0.15)', color: 'var(--accent)' }}
        >
          {label}
        </span>
        <DeviceIcon device={report.device} />
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {new Date(report.completedAt ?? report.createdAt).toLocaleString()}
        </span>
      </div>
      <p className="text-sm font-medium break-all">{report.url}</p>
    </div>
  )
}

const RESULT_COLOR: Record<AuditResult, string> = {
  pass: 'var(--score-good)',
  average: 'var(--score-average)',
  fail: 'var(--score-poor)',
  info: 'var(--text-muted)',
  na: 'var(--text-muted)',
}

function ResultIcon({ result }: { result: AuditResult }) {
  const color = RESULT_COLOR[result]
  const size = 13
  switch (result) {
    case 'pass':    return <CheckCircle2 size={size} style={{ color, flexShrink: 0 }} />
    case 'average': return <AlertCircle  size={size} style={{ color, flexShrink: 0 }} />
    case 'fail':    return <XCircle      size={size} style={{ color, flexShrink: 0 }} />
    case 'na':      return <Minus        size={size} style={{ color, flexShrink: 0 }} />
    default:        return <Info         size={size} style={{ color, flexShrink: 0 }} />
  }
}

export default function ComparisonPage() {
  const { idA, idB } = useParams<{ idA: string; idB: string }>()
  const navigate = useNavigate()

  const reportA = useQuery({
    queryKey: ['report', idA],
    queryFn: () => auditsApi.getReport(idA!),
    enabled: !!idA,
  })
  const reportB = useQuery({
    queryKey: ['report', idB],
    queryFn: () => auditsApi.getReport(idB!),
    enabled: !!idB,
  })

  // Order chronologically: older = "Before", newer = "After"
  const ordered = useMemo(() => {
    if (!reportA.data || !reportB.data) return null
    return timestampOf(reportA.data) <= timestampOf(reportB.data)
      ? { before: reportA.data, after: reportB.data }
      : { before: reportB.data, after: reportA.data }
  }, [reportA.data, reportB.data])

  const categoryDiffs = useMemo(() => {
    if (!ordered) return []
    return diffReports(ordered.before.rawReport, ordered.after.rawReport)
  }, [ordered])

  const isLoading = reportA.isLoading || reportB.isLoading
  const isError = reportA.isError || reportB.isError

  return (
    <PageTransition>
      <div className="max-w-5xl mx-auto px-4 py-12">
        <button
          onClick={() => navigate('/reports')}
          className="flex items-center gap-1.5 text-sm transition-colors mb-6"
          style={{ color: 'var(--text-secondary)' }}
        >
          <ArrowLeft size={14} /> Back to reports
        </button>

        {isLoading && (
          <div className="flex justify-center py-20">
            <Loader2 size={32} className="animate-spin" style={{ color: 'var(--accent)' }} />
          </div>
        )}

        {isError && (
          <GlassCard className="text-center py-12 space-y-3">
            <EmptyIllustration variant="error" size={96} className="mx-auto text-[var(--text-secondary)]" />
            <p className="text-red-400 text-sm">Failed to load one or both reports.</p>
          </GlassCard>
        )}

        {ordered && (
          <div className="space-y-4">
            {/* Two-column report headers */}
            <GlassCard>
              <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                <ReportHeader report={ordered.before} label="Before" />
                <ArrowRight
                  size={18}
                  className="hidden sm:block self-center shrink-0"
                  style={{ color: 'var(--text-muted)' }}
                />
                <ReportHeader report={ordered.after} label="After" />
              </div>
            </GlassCard>

            {/* Score comparison grid */}
            <GlassCard>
              <h2
                className="text-xs font-semibold uppercase tracking-wider mb-5"
                style={{ color: 'var(--text-muted)' }}
              >
                Score comparison
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
                {categoryDiffs.map(cat => (
                  <div key={cat.key} className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-4">
                      <ScoreRing
                        score={cat.scoreBefore ?? 0}
                        label="Before"
                        size={72}
                        strokeWidth={5}
                        animate={false}
                      />
                      <ArrowRight size={14} style={{ color: 'var(--text-muted)' }} />
                      <ScoreRing
                        score={cat.scoreAfter ?? 0}
                        label="After"
                        size={72}
                        strokeWidth={5}
                        animate={false}
                      />
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      <span className="text-sm font-semibold">{cat.label}</span>
                      <ScoreDelta before={cat.scoreBefore} after={cat.scoreAfter} />
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>

            {/* Changed audits */}
            <div className="space-y-2">
              <h3
                className="text-xs font-semibold uppercase tracking-wider px-1 mb-3"
                style={{ color: 'var(--text-muted)' }}
              >
                Changed audits
              </h3>

              {categoryDiffs.every(c => c.changed.length === 0) ? (
                <GlassCard className="text-center py-8">
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    No individual audit results changed between these reports.
                  </p>
                </GlassCard>
              ) : (
                categoryDiffs
                  .filter(c => c.changed.length > 0)
                  .map(cat => {
                    const regressions = cat.changed.filter(c => c.direction === -1).length
                    const improvements = cat.changed.filter(c => c.direction === 1).length
                    return (
                      <div
                        key={cat.key}
                        className="glass rounded-xl overflow-hidden"
                        style={{ border: '1px solid var(--border-subtle)' }}
                      >
                        <div
                          className="flex items-center gap-3 px-5 py-3"
                          style={{ borderBottom: '1px solid var(--border-subtle)' }}
                        >
                          <span className="text-sm font-semibold">{cat.label}</span>
                          <span
                            className="text-xs px-1.5 py-0.5 rounded-full"
                            style={{ background: 'rgba(255,255,255,0.04)', color: 'var(--text-muted)' }}
                          >
                            {cat.changed.length} changed
                          </span>
                          {regressions > 0 && (
                            <span
                              className="text-xs px-1.5 py-0.5 rounded-full font-medium"
                              style={{ background: 'rgba(239,68,68,0.12)', color: 'var(--score-poor)' }}
                            >
                              {regressions} regressed
                            </span>
                          )}
                          {improvements > 0 && (
                            <span
                              className="text-xs px-1.5 py-0.5 rounded-full font-medium"
                              style={{ background: 'rgba(34,197,94,0.12)', color: 'var(--score-good)' }}
                            >
                              {improvements} improved
                            </span>
                          )}
                        </div>
                        <div className="divide-y divide-white/5">
                          {cat.changed.map(audit => (
                            <div
                              key={audit.id}
                              className="grid grid-cols-[1fr_auto] items-center gap-3 px-5 py-3"
                            >
                              <span className="text-xs leading-relaxed">{audit.title}</span>
                              <div className="flex items-center gap-2 shrink-0">
                                <div className="flex items-center gap-1">
                                  <ResultIcon result={audit.before} />
                                  {audit.beforeDisplay && (
                                    <span
                                      className="text-[11px] font-mono tabular-nums"
                                      style={{ color: RESULT_COLOR[audit.before] }}
                                    >
                                      {audit.beforeDisplay}
                                    </span>
                                  )}
                                </div>
                                <ArrowRight size={11} style={{ color: 'var(--text-muted)' }} />
                                <div className="flex items-center gap-1">
                                  <ResultIcon result={audit.after} />
                                  {audit.afterDisplay && (
                                    <span
                                      className="text-[11px] font-mono tabular-nums"
                                      style={{ color: RESULT_COLOR[audit.after] }}
                                    >
                                      {audit.afterDisplay}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })
              )}
            </div>
          </div>
        )}
      </div>
    </PageTransition>
  )
}
